// Whether an outside lawyer has certified the signed POA on this case.
//
// Finanças returns NIF cases until the POA copy is certified, and the lawyer
// the POA names cannot certify it herself. The certifier works from their own
// queue; this panel is the lawyer's view of the result: waiting, certified
// (open the signed certification to attach in e-balcão) or declined (why).

import React, { useState } from 'react';
import { User } from 'firebase/auth';
import { PoaCertification, getPoaCertificationUrl, resetPoaCertification } from '../../api/certifications';
import '../CertificationPage/CertificationPage.css';

export interface PoaCertificationPanelProps {
  caseId: string;
  /** The id of the newest signed POA file on the case. */
  poaFileId: string;
  certification?: PoaCertification | null;
  user: User | null;
  onChanged: () => void;
}

export const certificationState = (
  certification: PoaCertification | null | undefined,
  poaFileId: string
): 'pending' | 'certified' | 'declined' => {
  if (!certification || certification.poa_file_id !== poaFileId) return 'pending';
  return certification.status;
};

const PoaCertificationPanel: React.FC<PoaCertificationPanelProps> = ({
  caseId,
  poaFileId,
  certification,
  user,
  onChanged,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const state = certificationState(certification, poaFileId);
  // A record for an older POA: the customer signed again after it.
  const stale = Boolean(certification) && certification?.poa_file_id !== poaFileId;

  const act = async (action: (token: string) => Promise<void>) => {
    if (!user) return;
    setBusy(true);
    setError('');
    try {
      await action(await user.getIdToken());
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const open = () =>
    act(async token => {
      window.open(await getPoaCertificationUrl(token, caseId), '_blank', 'noopener');
    });

  const sendBack = () =>
    act(async token => {
      await resetPoaCertification(token, caseId);
      onChanged();
    });

  const by = certification?.certifier_name
    ? `${certification.certifier_name}${certification.cedula ? ` (cédula ${certification.cedula})` : ''}`
    : 'the certifier';

  return (
    <div className={`poa-cert-panel poa-cert-panel--${state}`} data-testid="poa-certification">
      <h3>POA certification</h3>
      {state === 'pending' && (
        <p>
          Waiting for a certifier.
          {stale ? ' The customer signed the POA again after the last certification, so it needs a new one.' : ''}
        </p>
      )}
      {state === 'certified' && (
        <>
          <p>
            Certified by {by}. Ordem registration {certification?.registration_number}
            {certification?.consultation_code ? `, consultation code ${certification.consultation_code}` : ''}.
            {certification?.digitally_signed === false ? ' The upload has no digital signature (a scanned, hand-signed copy).' : ''}
          </p>
          <button className="financas-btn financas-btn--primary" onClick={open} disabled={busy}>
            Open certified POA
          </button>{' '}
          <button className="financas-btn financas-btn--link" onClick={sendBack} disabled={busy}>
            Certify again
          </button>
        </>
      )}
      {state === 'declined' && (
        <>
          <p>
            {by} declined: “{certification?.reason}”
          </p>
          <button className="financas-btn financas-btn--primary" onClick={sendBack} disabled={busy}>
            Send back to the certifier
          </button>
        </>
      )}
      {error && <div className="financas-office-error" role="alert">{error}</div>}
    </div>
  );
};

export default PoaCertificationPanel;
